import type { ActivityDefinition } from "@medplum/fhirtypes";
import { catalogNameToFhir, EXAM_DEFINITION_KIND, getTRPCClient, syncKey } from "@repo/api";
import {
  type ChangeExamDefinitionAvailabilityInput,
  type CorrectExamDefinitionInput,
  changeExamDefinitionAvailabilityInputSchema,
  correctExamDefinitionInputSchema,
  type DefineExamDefinitionInput,
  defineExamDefinitionInputSchema,
  type ReviseExamDefinitionInput,
  reviseExamDefinitionInputSchema,
} from "@repo/api/schemas";
import { mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { and, eq, ilike, max, or, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { readExamDefinitionRow } from "../fhir/exam-definition-storage";
import type { ActivityDefinitionRow } from "../fhir/types";
import { optimisticDefinedExam, optimisticRevisedExam } from "../lib/exam-definition-optimistic";

const EXAM_DEFINITION_COLLECTION = "activityDefinition";
/** Every exam canonical this app mints sits directly under this base. */
export const EXAM_DEFINITION_CANONICAL_BASE = canonicalBase("ActivityDefinition");
/**
 * One placeholder, as the authoring surface sees it. The registry-owned half —
 * label, note label, cardinality, options, reserved responses — is NOT here:
 * the editor reads it through `useFindingDefinitions`, and an exam author
 * cannot edit it at all.
 */
export interface ExamDefinitionFindingDto {
  /** The `FindingDefinition` lineage, bare canonical url. */
  findingDefinitionUrl: string;
  prerequisiteFindingUrl?: string;
}

export interface ExamDefinitionSectionDto {
  sectionId: string;
  label: string;
  findings: ExamDefinitionFindingDto[];
}

/** UI read model for Exam catalog listing/detail and Protocol authoring choices. */
export interface ExamDefinitionDto {
  definitionUrl: string;
  name: string;
  description?: string;
  availability: "Active" | "Inactive";
  version: number;
  authoredAt: string;
  examNoteLabel?: string;
  sections: ExamDefinitionSectionDto[];
  sectionCount: number;
  findingCount: number;
}

export interface ExamDefinitionFilters {
  availability?: ExamDefinitionDto["availability"];
  search?: string;
  /** Include pinned historical revisions when presenting ordered exam dependencies. */
  includePreviousVersions?: boolean;
}

const questionnaires = () => getDataStoreClient().getCollection(EXAM_DEFINITION_COLLECTION);

export function projectExamDefinition(resource: ActivityDefinition): ExamDefinitionDto {
  const facts = readExamDefinitionRow(resource);
  const sections = facts.sections.map((section) => ({
    sectionId: section.id,
    label: section.label,
    findings: section.findings.map((finding) => ({
      findingDefinitionUrl: finding.finding,
      prerequisiteFindingUrl: finding.prerequisite,
    })),
  }));
  return {
    definitionUrl: facts.spine.url,
    name: facts.spine.name,
    description: facts.spine.description,
    availability: facts.spine.availability,
    version: facts.spine.version,
    authoredAt: facts.spine.mintedAt,
    examNoteLabel: facts.examNoteLabel,
    sections,
    sectionCount: sections.length,
    findingCount: sections.reduce((count, section) => count + section.findings.length, 0),
  };
}

/** Mint once before dispatch so optimistic and authoritative writes share lineage identity. */
export function mintExamDefinitionUrl(): string {
  return canonicalUrl("ActivityDefinition", mintEntityId());
}

/** Current revisions by default; ordered work can request its historical dependency definitions too. */
export function useExamDefinitions(filters: ExamDefinitionFilters = {}): ExamDefinitionDto[] {
  const collection = useCollection(EXAM_DEFINITION_COLLECTION);
  const status =
    filters.availability === undefined ? undefined : filters.availability === "Active" ? "active" : "retired";
  const search = filters.search?.trim();
  const result = useLiveQuery({
    queryKey: [
      collection.id,
      "exam-definitions",
      status ?? null,
      search ?? null,
      filters.includePreviousVersions ?? false,
    ],
    query: (query) => {
      const latestVersions = query
        .from({ candidate: collection })
        .where(({ candidate }) => eq(candidate.catalogModelKind, EXAM_DEFINITION_KIND))
        .groupBy(({ candidate }) => candidate.url)
        .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
      const currentDefinitions = query
        .from({ definition: collection })
        .join({ latest: latestVersions }, ({ definition, latest }) => eq(definition.url, latest.url))
        .where(({ definition, latest }) =>
          and(eq(definition.catalogModelKind, EXAM_DEFINITION_KIND), eq(definition.catalogVersion, latest.version)),
        );
      const revisions = filters.includePreviousVersions
        ? query
            .from({ definition: collection })
            .where(({ definition }) => eq(definition.catalogModelKind, EXAM_DEFINITION_KIND))
        : currentDefinitions;
      const availableDefinitions = status
        ? revisions.where(({ definition }) => eq(definition.status, status))
        : revisions;
      const matchingDefinitions = search
        ? availableDefinitions.where(({ definition }) =>
            or(ilike(definition.title, `%${search}%`), ilike(definition.description, `%${search}%`)),
          )
        : availableDefinitions;
      return matchingDefinitions
        .orderBy(({ definition }) => definition.title, "asc")
        .fn.select(({ definition }) => projectExamDefinition(definition));
    },
  });
  return (result.data as ExamDefinitionDto[] | undefined) ?? [];
}

/** Current revision of one redesigned exam lineage. */
export function useExamDefinition(url: string | undefined): ExamDefinitionDto | undefined {
  const collection = useCollection(EXAM_DEFINITION_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "exam-definition", url ?? null],
    query: (query) => {
      if (!url) return undefined;
      return query
        .from({ definition: collection })
        .where(({ definition }) => and(eq(definition.catalogModelKind, EXAM_DEFINITION_KIND), eq(definition.url, url)))
        .orderBy(({ definition }) => definition.catalogVersion, "desc")
        .fn.select(({ definition }) => projectExamDefinition(definition))
        .findOne();
    },
  });
  return result.data as unknown as ExamDefinitionDto | undefined;
}

function currentExamResource(url: string): ActivityDefinitionRow {
  let current: ActivityDefinitionRow | undefined;
  for (const candidate of questionnaires().state.values()) {
    if (candidate.url !== url || candidate.catalogModelKind !== EXAM_DEFINITION_KIND) continue;
    if (!current || candidate.catalogVersion > current.catalogVersion) current = candidate;
  }
  if (!current?.id) throw new Error(`Exam Definition '${url}' is not available in the data store`);
  return current;
}

export const defineExamDefinition = defineWrite<DefineExamDefinitionInput>({
  optimistic: (params) => {
    const input = defineExamDefinitionInputSchema.parse(params);
    questionnaires().insert(optimisticDefinedExam(input, new Date().toISOString()));
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.examDefinition.define.mutate(input, options);
  },
});

export const reviseExamDefinition = defineWrite<ReviseExamDefinitionInput>({
  optimistic: (params) => {
    const input = reviseExamDefinitionInputSchema.parse(params);
    const currentResource = currentExamResource(input.definitionUrl);
    questionnaires().insert(optimisticRevisedExam(currentResource, input, new Date().toISOString()));
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.examDefinition.revise.mutate(input, options);
  },
});

export const correctExamDefinition = defineWrite<CorrectExamDefinitionInput>({
  optimistic: (params) => {
    const input = correctExamDefinitionInputSchema.parse(params);
    const resource = currentExamResource(input.definitionUrl);
    questionnaires().update(syncKey(resource), (draft) => {
      if (input.name !== undefined) Object.assign(draft, catalogNameToFhir(input.name));
      if (input.description === null) delete draft.description;
      else if (input.description !== undefined) draft.description = input.description;
    });
  },
  persist: async (input, options) => {
    await getTRPCClient().catalog.examDefinition.correct.mutate(input, options);
  },
});

function availabilityAction(availability: "Active" | "Inactive") {
  return defineWrite<ChangeExamDefinitionAvailabilityInput>({
    optimistic: (params) => {
      const input = changeExamDefinitionAvailabilityInputSchema.parse(params);
      const resource = currentExamResource(input.definitionUrl);
      questionnaires().update(syncKey(resource), (draft) => {
        draft.status = availability === "Active" ? "active" : "retired";
      });
    },
    persist: async (input, options) => {
      const mutation =
        availability === "Active"
          ? getTRPCClient().catalog.examDefinition.activate
          : getTRPCClient().catalog.examDefinition.deactivate;
      await mutation.mutate(input, options);
    },
  });
}

export const activateExamDefinition = availabilityAction("Active");
export const deactivateExamDefinition = availabilityAction("Inactive");
